import React, { useState, useEffect } from "react";
import MUITable from "Components/UI/Tables/MUITable";

import { Link } from "react-router-dom";
import { styled } from "@mui/material";
import AddBtn from "Components/UI/Tables/AddBtn";
import cutStrAtAndAddDots from "utils/cutStrAtAndAddDots";
import {
  getParseStatusLabelById,
  getSystemStatusLabelById,
} from "utils/labels";
import {
  getSystemStatusColorByLabel,
  getParseStatusColorByLabel,
} from "utils/colors";
import CommonChip from "Components/UI/Tables/CommonChip";
import useTableOptions from "Hooks/useTableOptions";
import { useAuth } from "Hooks/useAuth";
import { useNavigate } from "react-router-dom";

const StyledTable = styled(MUITable)(({ theme }) => ({
  backgroundColor: "#ffff",
  border: "none",
  boxShadow: "none",
  ".MuiTable-root": {
    ".MuiTableCell-root": {
      "&:nth-of-type(1)": {
        paddingTop: "0",
        paddingBottom: "0",
      },
    },
  },
}));

const createRows = (endcustomers) => {
  return endcustomers?.map((c) => {
    return [
      { label: c.ExternalCustomerNr, id: c.Id },
      { label: c.Name, id: c.Id },
      c.Name2,
      c.Address,
      c.Address2,
      c.PostalCode,
      c.City,
      c.CountryCode,
      c.OrgNo,
      c.ContactEmail,
      getParseStatusLabelById(c.ParseStatus),
      cutStrAtAndAddDots(c.ParseMessage, 45),
      getSystemStatusLabelById(c.SystemStatus),
      c.CreateTime,
    ];
  });
};
const columnsToHideClient = ["Kontrollstatus", "Kontrollmeddelande", "Status"];

export default function CustomerTable({ customers, openCreateEndcustomer }) {
  const [rows, setRows] = useState(createRows(customers));
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const { handleColumnShow, isColDisplayed, isNew } = useTableOptions(
    "EndcustomersMainTable"
  );

  useEffect(() => {
    if (isNew && !isAdmin) {
      for (const col of columnsToHideClient) {
        handleColumnShow(col, "remove");
      }
    }
  }, [isAdmin, isNew]);

  useEffect(() => {
    setRows(createRows(customers));
  }, [customers]);

  // If col names are changed, change in customSearch in Options for table
  const columns = [
    {
      label: "Kundnummer",
      name: "Kundnummer",
      options: {
        display: isColDisplayed("Kundnummer"),
        filter: false,
        customBodyRender: (c) => {
          return isAdmin ? (
            <Link to={`/endcustomers/${c.id}`}>{c.label}</Link>
          ) : (
            <Link onClick={() => navigate(`/mypages/endcustomers/${c.id}`)}>
              {c.label ? c.label : "Saknas"}
            </Link>
          );
        },
      },
    },
    {
      label: "Kundnamn",
      name: "Kundnamn",
      options: {
        display: isColDisplayed("Kundnamn"),
        customBodyRender: (c) => {
          return isAdmin ? (
            <Link to={`/endcustomers/${c.id}`}>{c.label}</Link>
          ) : (
            c.label
          );
        },
      },
    },
    {
      label: "Namn2",
      name: "Namn2",
      options: {
        display: isColDisplayed("Namn2"),
        filter: false,
      },
    },
    {
      label: "Adress",
      name: "Adress",
      options: { display: isColDisplayed("Adress"), filter: false },
    },
    {
      label: "Adress2",
      name: "Adress2",
      options: { display: isColDisplayed("Adress2"), filter: false },
    },
    {
      label: "Postnr",
      name: "Postnr",
      options: {
        display: isColDisplayed("Postnr"),
        filter: false,
      },
    },
    {
      label: "Post ort",
      name: "PostOrt",
      options: {
        display: isColDisplayed("PostOrt"),
        filter: false,
      },
    },
    {
      label: "Landkod",
      name: "Landkod",
      options: {
        display: isColDisplayed("Landkod"),
        filter: false,
      },
    },
    {
      label: "Org.nr",
      name: "Org.NR",
      options: {
        display: isColDisplayed("Org.NR"),
        filter: false,
      },
    },
    {
      label: "Epostadress",
      name: "Epostadress",
      options: {
        display: isColDisplayed("Epostadress"),
        filter: false,
      },
    },
    {
      name: "Kontrollstatus",
      options: {
        filter: true,
        display: isColDisplayed("Kontrollstatus"),
        sort: true,
        customBodyRender: (value) => {
          return (
            <CommonChip
              labelName={value}
              color={getParseStatusColorByLabel(value)}
            />
          );
        },
      },
    },
    {
      name: "Kontrollmeddelande",
      options: {
        filter: false,
        display: isColDisplayed("Kontrollmeddelande"),
      },
    },
    {
      name: "Status",
      options: {
        filter: true,
        display: isColDisplayed("Status"),
        sort: true,
        customBodyRender: (value) => {
          return (
            <CommonChip
              labelName={value}
              color={getSystemStatusColorByLabel(value)}
            />
          );
        },
      },
    },
    {
      name: "Skapad",
      options: {
        filter: false,
        display: isColDisplayed("Skapad"),
      },
    },
  ];

  const options = {
    onViewColumnsChange: (changedColumn, action) => {
      handleColumnShow(changedColumn, action);
    },
    selectableRowsHeader: false,
    customToolbar: () => {
      return (
        <>
          <AddBtn onClick={openCreateEndcustomer} title={"Skapa slutkund"} />
        </>
      );
    },
    selectableRowsHideCheckboxes: true,
    filterType: "dropdown",
    responsive: "standard",
    downloadOptions: {
      separator: ";",
      filename: "all_clients.csv",
    },
    customSearch: (searchQuery, currentRow, columns) => {
      let result = false;

      columns.forEach((col, index) => {
        if (!col.searchable) {
          return;
        }

        if (!currentRow[index]) {
          return;
        }

        const cmpVal =
          currentRow[index] !== null && typeof currentRow[index] === "object"
            ? currentRow[index].label ?? ""
            : String(currentRow[index]);

        if (
          cmpVal &&
          cmpVal.toLowerCase().includes(searchQuery.toLowerCase())
        ) {
          result = true;
          return;
        }
      });

      return result;
    },
    onDownload: (buildHead, buildBody, columns, data) => {
      const csvData = data.map(({ data, index }) => {
        const rowData = data.map((r1, index) => {
          if (typeof r1 === "object" && r1 !== null && r1.label !== null) {
            return r1.label;
          }
          return r1;
        });
        return { index: index, data: rowData };
      });
      return "\uFEFF" + buildHead(columns) + buildBody(csvData);
    },
  };

  return (
    <>
      <StyledTable options={options} data={rows} columns={columns} />
    </>
  );
}
